"use client";

import { useCallback, useMemo } from "react";
import { useProposalMutations } from "@stockline/web";
import type { PreOrder } from "@/app/api/types/preOrder";
import { findPreOrderProposal } from "../utils/proposalContext";

interface UseProposalHeaderHandlerOptions {
  preOrder: PreOrder;
}

const HEADER_FIELDS = new Set([
  "partner_id",
  "employee_id",
  "order_date",
  "delivery_date",
  "comment",
  "pre_order_number",
  "customer_po_number",
  "type",
  "category",
  "status",
  "pre_order_metadata",
  "location_id",
  "route_id",
  "shipping_address_id",
]);

/**
 * When the active pre-order is a pending proposal, provide a `dispatch`
 * function that patches the parent pre_order Proposal payload via
 * `editProposal` instead of mutating the live entity. Returns `null` for
 * non-proposal pre-orders so callers fall back to their normal mutation.
 */
export function useProposalHeaderHandler({
  preOrder,
}: UseProposalHeaderHandlerOptions): {
  dispatch: (data: Record<string, unknown>) => Promise<void>;
} | null {
  const { editProposal } = useProposalMutations();

  const ctx = useMemo(() => findPreOrderProposal(preOrder), [preOrder]);

  const dispatch = useCallback(
    async (data: Record<string, unknown>): Promise<void> => {
      if (!ctx) return;
      const filtered: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(data)) {
        if (HEADER_FIELDS.has(key)) filtered[key] = value;
      }
      if (Object.keys(filtered).length === 0) return;
      await editProposal(ctx.batchId, ctx.proposalId, filtered);
    },
    [ctx, editProposal],
  );

  if (!ctx) return null;
  return { dispatch };
}
